import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, Search } from "lucide-react";
import type { Species } from "@/data/species/types/Species";
import {
  formatSpeciesSeasonLabel,
  getAllSpecies,
  isFieldGuideVisible,
  isInSeason,
} from "@/utils/speciesUtils";
import {
  fieldGuideArticlePath,
  fieldGuideSearchPath,
  rankSpeciesSearchSuggestions,
} from "@/utils/species/resolveSpeciesSearch";
import "./HeaderSearch.css";

const SPECIES_LIMIT = 5;
const PLACE_LIMIT = 5;
const IN_SEASON_LIMIT = 4;
const PLACE_DEBOUNCE_MS = 220;

type PlaceResult = {
  gazId: string;
  name: string;
  featureClass: string;
  stateAlpha: string;
};

type ResultItem =
  | { kind: "species"; key: string; species: Species; path: string }
  | { kind: "place"; key: string; place: PlaceResult; path: string }
  | { kind: "all"; key: string; path: string };

/** Summary catalog, fetched once on first focus and shared across header mounts. */
let speciesPromise: Promise<Species[]> | null = null;
function loadSpecies(): Promise<Species[]> {
  if (!speciesPromise) {
    speciesPromise = getAllSpecies({ summary: true }).catch((error) => {
      speciesPromise = null;
      throw error;
    });
  }
  return speciesPromise;
}

async function fetchPlaces(q: string, signal: AbortSignal): Promise<PlaceResult[]> {
  const params = new URLSearchParams({ q, limit: String(PLACE_LIMIT) });
  const res = await fetch(`/api/geo/catalog/search?${params.toString()}`, { signal });
  if (!res.ok) return [];
  const json = (await res.json()) as { features?: unknown[] };
  const features = Array.isArray(json?.features) ? json.features : [];
  return features
    .map((f) => {
      const props = (f as { properties?: Record<string, unknown> })?.properties ?? {};
      return {
        gazId: String(props.gaz_id ?? ""),
        name: String(props.name ?? ""),
        featureClass: String(props.gaz_featureclass ?? ""),
        stateAlpha: String(props.state_alpha ?? ""),
      };
    })
    .filter((p) => p.gazId && p.name);
}

function placeSubtitle(place: PlaceResult): string {
  return [place.featureClass, place.stateAlpha].filter(Boolean).join(" · ");
}

function SpeciesThumb({ species }: { species: Species }) {
  const [failed, setFailed] = useState(false);
  if (species.imageUrl && !failed) {
    return (
      <img
        className="header-search__thumb"
        src={species.imageUrl}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="header-search__thumb header-search__thumb--species" aria-hidden>
      {species.name.slice(0, 2)}
    </span>
  );
}

type HeaderSearchProps = {
  /** Phone layout: full-width field with a Cancel button; parent controls visibility. */
  variant?: "inline" | "sheet";
  onClose?: () => void;
};

const HeaderSearch: React.FC<HeaderSearchProps> = ({ variant = "inline", onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const listboxId = useId();
  const inputId = useId();
  const rootRef = useRef<HTMLFormElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [species, setSpecies] = useState<Species[]>([]);
  const [speciesError, setSpeciesError] = useState(false);
  const [places, setPlaces] = useState<PlaceResult[]>([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmed = query.trim();

  const ensureSpecies = useCallback(() => {
    if (species.length) return;
    loadSpecies()
      .then((list) => {
        setSpecies(list);
        setSpeciesError(false);
      })
      .catch(() => setSpeciesError(true));
  }, [species.length]);

  // Sheet opens already focused.
  useEffect(() => {
    if (variant === "sheet") {
      inputRef.current?.focus();
      setOpen(true);
      ensureSpecies();
    }
  }, [variant, ensureSpecies]);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
    setQuery("");
  }, [location.pathname]);

  // ⌘K / Ctrl+K focuses the inline field.
  useEffect(() => {
    if (variant !== "inline") return undefined;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        const input = inputRef.current;
        // Hidden at phone width; the sheet trigger handles it there.
        if (!input || input.offsetParent === null) return;
        event.preventDefault();
        input.focus();
        input.select();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [variant]);

  useEffect(() => {
    if (variant !== "inline") return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [variant]);

  useEffect(() => {
    if (trimmed.length < 2) {
      setPlaces([]);
      setPlacesLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setPlacesLoading(true);
    const timer = window.setTimeout(() => {
      fetchPlaces(trimmed, controller.signal)
        .then((list) => setPlaces(list))
        .catch(() => {
          if (!controller.signal.aborted) setPlaces([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setPlacesLoading(false);
        });
    }, PLACE_DEBOUNCE_MS);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [trimmed]);

  const speciesMatches = useMemo(
    () => (trimmed ? rankSpeciesSearchSuggestions(species, trimmed, SPECIES_LIMIT) : []),
    [species, trimmed],
  );

  const inSeason = useMemo(
    () =>
      species
        .filter((s) => isFieldGuideVisible(s) && isInSeason(s) && s.slug)
        .slice(0, IN_SEASON_LIMIT),
    [species],
  );

  const items: ResultItem[] = useMemo(() => {
    const out: ResultItem[] = [];
    const speciesList = trimmed ? speciesMatches : inSeason;
    speciesList.forEach((s) => {
      const path = fieldGuideArticlePath(s);
      if (path) out.push({ kind: "species", key: `s:${s.slug}`, species: s, path });
    });
    if (trimmed) {
      places.forEach((p) =>
        out.push({ kind: "place", key: `p:${p.gazId}`, place: p, path: `/places/${p.gazId}` }),
      );
      out.push({ kind: "all", key: "all", path: fieldGuideSearchPath(trimmed) });
    }
    return out;
  }, [trimmed, speciesMatches, inSeason, places]);

  useEffect(() => {
    setActiveIndex(trimmed && items.length > 1 ? 0 : -1);
  }, [trimmed, items.length]);

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
    onClose?.();
    navigate(path);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const active = activeIndex >= 0 ? items[activeIndex] : null;
    if (active) {
      go(active.path);
    } else if (trimmed) {
      go(fieldGuideSearchPath(trimmed));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!items.length) return;
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((i) => (i + step + items.length) % items.length);
    } else if (event.key === "Escape") {
      if (variant === "sheet") {
        onClose?.();
      } else if (query) {
        setQuery("");
      } else {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
  };

  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(`${listboxId}-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, listboxId]);

  const speciesItems = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.kind === "species");
  const placeItems = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.kind === "place");
  const allIndex = items.findIndex((item) => item.kind === "all");

  const showPanel = open && (trimmed.length > 0 || inSeason.length > 0 || speciesError);
  const nothingFound =
    trimmed.length > 0 &&
    species.length > 0 &&
    speciesMatches.length === 0 &&
    places.length === 0 &&
    !placesLoading;

  const optionProps = (index: number, path: string) => ({
    id: `${listboxId}-${index}`,
    role: "option" as const,
    "aria-selected": index === activeIndex,
    className: `header-search__row${index === activeIndex ? " is-active" : ""}`,
    onMouseEnter: () => setActiveIndex(index),
    // pointerdown keeps focus in the input so the panel doesn't close first.
    onPointerDown: (event: React.PointerEvent) => event.preventDefault(),
    onClick: () => go(path),
  });

  return (
    <form
      ref={rootRef}
      role="search"
      className={`header-search header-search--${variant}`}
      onSubmit={handleSubmit}
    >
      <label htmlFor={inputId} className="header-search__label">
        Search species and places
      </label>
      <div className="header-search__field">
        <Search className="header-search__icon" size={16} strokeWidth={2} aria-hidden />
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          className="header-search__input"
          placeholder="Search species and places"
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showPanel && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
          }
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            ensureSpecies();
          }}
          onKeyDown={handleKeyDown}
        />
        {variant === "inline" ? (
          <kbd className="header-search__kbd" aria-hidden>
            ⌘K
          </kbd>
        ) : null}
      </div>
      {variant === "sheet" ? (
        <button type="button" className="header-search__cancel" onClick={onClose}>
          Cancel
        </button>
      ) : null}

      <div
        id={listboxId}
        role="listbox"
        aria-label="Search results"
        className="header-search__panel"
        hidden={!showPanel}
      >
        {speciesError && !species.length ? (
          <p className="header-search__empty">Species couldn’t load. Check your connection and try again.</p>
        ) : null}

        {speciesItems.length ? (
          <div className="header-search__group" role="group" aria-label={trimmed ? "Species" : "In season"}>
            <div className="header-search__group-label">{trimmed ? "Species" : "In season"}</div>
            {speciesItems.map(({ item, index }) =>
              item.kind === "species" ? (
                <div key={item.key} {...optionProps(index, item.path)}>
                  <SpeciesThumb species={item.species} />
                  <span className="header-search__text">
                    <span className="header-search__title">
                      {item.species.name}
                      {item.species.scientificName ? (
                        <i className="header-search__sci">{item.species.scientificName}</i>
                      ) : null}
                    </span>
                    <span className="header-search__sub">
                      Season {formatSpeciesSeasonLabel(item.species.season)}
                    </span>
                  </span>
                  {isInSeason(item.species) ? (
                    <span className="header-search__chip">In season</span>
                  ) : null}
                </div>
              ) : null,
            )}
          </div>
        ) : null}

        {placeItems.length ? (
          <div className="header-search__group" role="group" aria-label="Places">
            <div className="header-search__group-label">Places</div>
            {placeItems.map(({ item, index }) =>
              item.kind === "place" ? (
                <div key={item.key} {...optionProps(index, item.path)}>
                  <span className="header-search__thumb header-search__thumb--place" aria-hidden>
                    <MapPin size={16} strokeWidth={2} />
                  </span>
                  <span className="header-search__text">
                    <span className="header-search__title">{item.place.name}</span>
                    <span className="header-search__sub">{placeSubtitle(item.place)}</span>
                  </span>
                </div>
              ) : null,
            )}
          </div>
        ) : null}

        {nothingFound ? (
          <p className="header-search__empty">
            No species or places match “{trimmed}”. Try a common name like “chanterelle” or a place
            like “Pisgah”.
          </p>
        ) : null}

        {allIndex >= 0 && !nothingFound ? (
          <div className="header-search__group header-search__group--footer">
            <div {...optionProps(allIndex, items[allIndex].path)}>
              <span className="header-search__thumb header-search__thumb--all" aria-hidden>
                <Search size={15} strokeWidth={2} />
              </span>
              <span className="header-search__text">
                <span className="header-search__title">
                  Search the Field Guide for “{trimmed}”
                </span>
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </form>
  );
};

/** Phone header: a search icon that opens the sheet variant under the bar. */
export const HeaderSearchTrigger: React.FC<{ onOpen: () => void; expanded: boolean; controls: string }> = ({
  onOpen,
  expanded,
  controls,
}) => (
  <button
    type="button"
    className="header-search-trigger"
    onClick={onOpen}
    aria-label="Search species and places"
    aria-expanded={expanded}
    aria-controls={controls}
  >
    <Search size={20} strokeWidth={2} aria-hidden />
  </button>
);

export const HeaderSearchSheet: React.FC<{ id: string; onClose: () => void }> = ({ id, onClose }) => (
  <>
    <div className="header-search-sheet__backdrop" aria-hidden onClick={onClose} />
    <div id={id} className="header-search-sheet">
      <HeaderSearch variant="sheet" onClose={onClose} />
    </div>
  </>
);

export default HeaderSearch;
